import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, MUHASEBE_AD, formatTl } from '../lib/api';
import Ipucu from './Ipucu';

/**
 * AY AY CİRO VE ALIŞ — ayrıntılı tablo (Raporlar › Finans'ta grafiğin altında durur).
 * Rakamlar muhasebe programının faturalarından gelir; dönem seçiminden bağımsızdır (paneldeki bütün aylar).
 * Ayın notları (numarasız fatura, ara toplam − iskonto, ileri tarihli) düğmeyle açılır.
 */
type Ay = {
  ay: string; satisAdet: number; iadeAdet: number; ciroHaricKurus: number; ciroDahilKurus: number; satisKalanKurus: number;
  alisAdet: number; alisHaricKurus: number; alisDahilKurus: number; alisKalanKurus: number;
  faturasizAdet: number; faturasizKurus: number;
  ciroBrutKurus: number; faturaIskontoKurus: number;
  numarasizAdet: number; numarasizKurus: number; ileriTarihliAdet: number; ileriTarihliKurus: number;
};
type Aylik = { aylar: Ay[]; faturasizGorunur: boolean; ilkFaturaTarihi: string | null };

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const ayAdi = (ay: string) => `${AYLAR[Number(ay.slice(5)) - 1]} ${ay.slice(0, 4)}`;
const ayAraligi = (ay: string) => { const [y, m] = ay.split('-').map(Number); const son = new Date(Date.UTC(y, m, 0)).getUTCDate(); return `bas=${ay}-01&bit=${ay}-${String(son).padStart(2, '0')}`; };

export default function AylikTablo() {
  const [aylik, setAylik] = useState<Aylik | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /** KDV dahil sütunları varsayılan gizli: tablo ekrana sığsın. */
  const [kdvDahil, setKdvDahil] = useState(false);
  const [acikNot, setAcikNot] = useState<Set<string>>(new Set());
  const notAcKapa = (ay: string) => setAcikNot((s) => { const y = new Set(s); if (y.has(ay)) y.delete(ay); else y.add(ay); return y; });
  useEffect(() => { api<Aylik>('/api/finans/aylik').then(setAylik).catch((e) => setHata((e as Error).message)); }, []);
  const enBuyuk = Math.max(1, ...(aylik?.aylar ?? []).flatMap((a) => [a.ciroHaricKurus, a.alisHaricKurus]));

  if (hata) return <div className="ccd-empty">{hata}</div>;
  if (!aylik) return <div className="ccd-empty">Yükleniyor…</div>;
  if (aylik.aylar.length === 0) return <div className="ccd-empty">Henüz fatura yok.</div>;
  return (
    <div className="ccd-stack-sm ccd-fin-sayfa">
      <label className="ccd-row" style={{ gap: 6, fontSize: 13, cursor: 'pointer', justifyContent: 'flex-end', padding: '8px 16px 0' }}>
        <input type="checkbox" checked={kdvDahil} onChange={(e) => setKdvDahil(e.target.checked)} /> KDV dahil sütunları göster
      </label>
        <div className="ccd-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="ccd-table">
            <thead><tr className="ccd-tr">
              <th className="ccd-th">Ay</th>
              <th className="ccd-th ccd-td-num">Ciro (KDV hariç) <Ipucu id="ciro" /></th>
              {kdvDahil && <th className="ccd-th ccd-td-num">Ciro (KDV dahil) <Ipucu id="ciro-kdv-dahil" /></th>}
              <th className="ccd-th ccd-td-num">Tahsil edilmemiş <Ipucu id="tahsil-edilmemis" /></th>
              <th className="ccd-th ccd-td-num">Alış (KDV hariç) <Ipucu id="alis-toplami" /></th>
              {kdvDahil && <th className="ccd-th ccd-td-num">Alış (KDV dahil) <Ipucu id="kdv-haric-dahil" /></th>}
              <th className="ccd-th ccd-td-num">Ödenmemiş alış <Ipucu id="tedarikci-borcu" /></th>
              {aylik.faturasizGorunur && <th className="ccd-th ccd-td-num">Faturasız çıkış <Ipucu id="faturasiz-cikis" /></th>}
            </tr></thead>
            <tbody>
              {aylik.aylar.map((a) => (
                <Fragment key={a.ay}>
                <tr className={`ccd-tr${acikNot.has(a.ay) ? ' ccd-fin-notlu' : ''}`}>
                  <td className="ccd-td ccd-fin-ay">
                    <Link to={`/rapor/satis?${ayAraligi(a.ay)}`} className="ccd-fin-ay-ad" title="Bu ayın satış raporunu aç">{ayAdi(a.ay)}</Link>
                    <div className="ccd-meta">{a.satisAdet} satış{a.iadeAdet ? ` · ${a.iadeAdet} iade` : ''} · {a.alisAdet} alış
                      {(() => { const n = (a.numarasizAdet > 0 ? 1 : 0) + (a.faturaIskontoKurus > 0 ? 1 : 0) + (a.ileriTarihliAdet > 0 ? 1 : 0);
                        return n > 0 ? <button type="button" className={`ccd-fin-not-dugme${a.numarasizAdet > 0 ? ' ccd-fin-not-dugme-uyari' : ''}`} aria-expanded={acikNot.has(a.ay)}
                                               onClick={() => notAcKapa(a.ay)} title={a.numarasizAdet > 0 ? 'Numarası olmayan fatura var — ayrıntı için tıklayın' : 'Bu ayın notları'}>
                          {n} not {acikNot.has(a.ay) ? '▴' : '▾'}</button> : null; })()}
                    </div></td>
                  <td className="ccd-td ccd-td-num"><b>{formatTl(a.ciroHaricKurus)}</b>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--ccd-accent, #c2703f)', width: `${Math.max(2, (a.ciroHaricKurus / enBuyuk) * 100)}%`, marginLeft: 'auto', marginTop: 4 }} /></td>
                  {kdvDahil && <td className="ccd-td ccd-td-num">{formatTl(a.ciroDahilKurus)}</td>}
                  <td className="ccd-td ccd-td-num">{formatTl(a.satisKalanKurus)}</td>
                  <td className="ccd-td ccd-td-num"><b>{formatTl(a.alisHaricKurus)}</b>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--ccd-text)', opacity: .55, width: `${Math.max(2, (a.alisHaricKurus / enBuyuk) * 100)}%`, marginLeft: 'auto', marginTop: 4 }} /></td>
                  {kdvDahil && <td className="ccd-td ccd-td-num">{formatTl(a.alisDahilKurus)}</td>}
                  <td className="ccd-td ccd-td-num">{formatTl(a.alisKalanKurus)}</td>
                  {aylik.faturasizGorunur && <td className="ccd-td ccd-td-num">{a.faturasizAdet ? formatTl(a.faturasizKurus) : <span className="ccd-muted">—</span>}</td>}
                </tr>
                {acikNot.has(a.ay) && (
                  <tr className="ccd-fin-not-satir">
                    <td className="ccd-td" colSpan={(aylik.faturasizGorunur ? 6 : 5) + (kdvDahil ? 2 : 0)}>
                      <div className="ccd-fin-notlar">
                        {a.numarasizAdet > 0 && <span className="ccd-fin-not ccd-fin-not-uyari">
                          {a.numarasizAdet} satışın fatura numarası yok · {formatTl(a.numarasizKurus)} · ciroya dahil <Ipucu id="numarasiz-fatura" /></span>}
                        {a.faturaIskontoKurus > 0 && <span className="ccd-fin-not">
                          {MUHASEBE_AD} ara toplamı {formatTl(a.ciroBrutKurus)} − fatura iskontosu {formatTl(a.faturaIskontoKurus)} = ciro <Ipucu id="parasut-ara-toplam" /></span>}
                        {a.ileriTarihliAdet > 0 && <span className="ccd-fin-not">
                          {a.ileriTarihliAdet} ileri tarihli fatura · {formatTl(a.ileriTarihliKurus)} · dahil</span>}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              {(() => { const t = aylik.aylar.reduce((x, a) => ({ ciro: x.ciro + a.ciroHaricKurus, ciroD: x.ciroD + a.ciroDahilKurus, kalan: x.kalan + a.satisKalanKurus,
                  alis: x.alis + a.alisHaricKurus, alisD: x.alisD + a.alisDahilKurus, alisK: x.alisK + a.alisKalanKurus, fsz: x.fsz + (a.faturasizKurus ?? 0) }),
                  { ciro: 0, ciroD: 0, kalan: 0, alis: 0, alisD: 0, alisK: 0, fsz: 0 });
                return (
                  <tr className="ccd-tr ccd-fin-toplam">
                    <td className="ccd-td"><b>Toplam</b><div className="ccd-meta">{aylik.aylar.length} ay</div></td>
                    <td className="ccd-td ccd-td-num"><b>{formatTl(t.ciro)}</b></td>
                    {kdvDahil && <td className="ccd-td ccd-td-num">{formatTl(t.ciroD)}</td>}
                    <td className="ccd-td ccd-td-num">{formatTl(t.kalan)}</td>
                    <td className="ccd-td ccd-td-num"><b>{formatTl(t.alis)}</b></td>
                    {kdvDahil && <td className="ccd-td ccd-td-num">{formatTl(t.alisD)}</td>}
                    <td className="ccd-td ccd-td-num">{formatTl(t.alisK)}</td>
                    {aylik.faturasizGorunur && <td className="ccd-td ccd-td-num">{t.fsz ? formatTl(t.fsz) : <span className="ccd-muted">—</span>}</td>}
                  </tr>
                ); })()}
            </tfoot>
          </table>
        </div>
    </div>
  );
}
